const DEFAULT_PORTAL_FAVICON_HREF = '/static/openteam-ai-logo-v3.png';
const LOADING_FAVICON_SIZE_PX = 64;
const LOADING_BADGE_RADIUS_PX = 10;
const LOADING_BADGE_OFFSET_PX = 4;
const LOADING_BADGE_FILL = '#22c55e';
const LOADING_BADGE_STROKE = '#ffffff';
const LOADING_FAVICON_CACHE = new Map<string, Promise<string>>();

type BuildPortalBrowserTitleOptions = {
	defaultBrandName: string;
	newWorkTitle?: string;
	portalBrandName: string;
	portalHeaderTitle: string;
	showNewWorkTitle?: boolean;
};

export const buildPortalBrowserTitle = ({
	defaultBrandName,
	newWorkTitle = 'New task',
	portalBrandName,
	portalHeaderTitle,
	showNewWorkTitle = false
}: BuildPortalBrowserTitleOptions) => {
	const teamLabel = portalBrandName || defaultBrandName;
	const headerTitle = portalHeaderTitle.trim();

	if (!headerTitle) {
		return teamLabel;
	}

	if (headerTitle === newWorkTitle) {
		return showNewWorkTitle ? newWorkTitle : teamLabel;
	}

	if (headerTitle.toLowerCase() === newWorkTitle.toLowerCase()) {
		return showNewWorkTitle ? headerTitle : teamLabel;
	}

	return headerTitle;
};

export const isPortalNewChatRoutePath = (pathname: string) => {
	const path = String(pathname || '')
		.trim()
		.split(/[?#]/, 1)[0]
		.replace(/\/+$/, '');

	return /^\/t\/[^/]+\/new$/.test(path);
};

export const resolvePortalBaseFaviconHref = (doc: Document | null | undefined) =>
	doc?.querySelector<HTMLLinkElement>('link[rel="icon"]')?.href ?? DEFAULT_PORTAL_FAVICON_HREF;

const loadImageElement = (src: string) =>
	new Promise<HTMLImageElement>((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error(`Failed to load favicon image: ${src}`));
		image.src = src;
	});

export const createPortalLoadingFaviconHref = async (baseFaviconHref: string) => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return baseFaviconHref;
	}

	const resolvedBaseFaviconHref = baseFaviconHref || DEFAULT_PORTAL_FAVICON_HREF;
	const cachedFaviconPromise = LOADING_FAVICON_CACHE.get(resolvedBaseFaviconHref);
	if (cachedFaviconPromise) {
		return cachedFaviconPromise;
	}

	const loadingFaviconPromise = (async () => {
		try {
			const response = await fetch(resolvedBaseFaviconHref, { credentials: 'include' });
			if (!response.ok) {
				throw new Error(`Failed to fetch favicon: ${response.status}`);
			}

			const faviconBlob = await response.blob();
			const faviconObjectUrl = URL.createObjectURL(faviconBlob);

			try {
				const iconImage = await loadImageElement(faviconObjectUrl);
				const canvas = document.createElement('canvas');
				canvas.width = LOADING_FAVICON_SIZE_PX;
				canvas.height = LOADING_FAVICON_SIZE_PX;

				const context = canvas.getContext('2d');
				if (!context) {
					return resolvedBaseFaviconHref;
				}

				context.clearRect(0, 0, LOADING_FAVICON_SIZE_PX, LOADING_FAVICON_SIZE_PX);
				context.drawImage(iconImage, 0, 0, LOADING_FAVICON_SIZE_PX, LOADING_FAVICON_SIZE_PX);

				const badgeCenter =
					LOADING_FAVICON_SIZE_PX - LOADING_BADGE_RADIUS_PX - LOADING_BADGE_OFFSET_PX;

				context.beginPath();
				context.arc(badgeCenter, badgeCenter, LOADING_BADGE_RADIUS_PX, 0, Math.PI * 2);
				context.fillStyle = LOADING_BADGE_FILL;
				context.fill();
				context.lineWidth = 3;
				context.strokeStyle = LOADING_BADGE_STROKE;
				context.stroke();

				return canvas.toDataURL('image/png');
			} finally {
				URL.revokeObjectURL(faviconObjectUrl);
			}
		} catch (error) {
			console.error('Failed to build Portal loading favicon', error);
			return resolvedBaseFaviconHref;
		}
	})();

	LOADING_FAVICON_CACHE.set(resolvedBaseFaviconHref, loadingFaviconPromise);
	return loadingFaviconPromise;
};
